import React from 'react';
import { Dayjs } from 'dayjs';
import { Text } from 'components/Typography';
import { centsToDollarsDisplay, formatDateForUILongFormat, ITipCalculatorTipDataTotals } from 'utils';
import Sidebar from './components/Sidebar';
import { Content, ContentSection, RowItem } from './components/styles';

interface ITipCalcOtherContributorsSidebarProps {
  dataCy?: string;
  businessDate: Dayjs;
  totals: ITipCalculatorTipDataTotals;
  creditCardPercentFee?: number;
  onBack: () => void;
}

const TipCalcOtherContributorsSidebar = ({ businessDate, totals, creditCardPercentFee, onBack, dataCy = 'tip-calc-other-contributors-sidebar' }: ITipCalcOtherContributorsSidebarProps) => (
  <Sidebar title="Other Contributors Summary" leftActionButton={{ label: 'Back', appearance: 'white', onClick: onBack }} dataCy={dataCy}>
    <Content>
      <RowItem data-cy={`${dataCy}-business-date`} className="business-date">
        <Text size="small" data-cy={`${dataCy}-business-date-label`}>
          Business Date:
        </Text>
        <Text size="small" data-cy={`${dataCy}-business-date-value`}>
          {formatDateForUILongFormat(businessDate)}
        </Text>
      </RowItem>
      <ContentSection data-cy={`${dataCy}-collected-tips-and-autograts-section`}>
        <RowItem data-cy={`${dataCy}-total`} className="total">
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-total-label`}>
            Collected Tips & Autograts
          </Text>
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-total-value`}>
            {centsToDollarsDisplay(totals.collectedTipsAndAutograts || 0)}
          </Text>
        </RowItem>
      </ContentSection>
      <ContentSection data-cy={`${dataCy}-tip-details-section`}>
        <Text size="small" fontWeight="medium" data-cy={`${dataCy}-tip-details-section-header`}>
          Other Contributors Details
        </Text>
        <RowItem data-cy={`${dataCy}-cc-tips`}>
          <Text size="small" data-cy={`${dataCy}-cc-tips-label`}>
            Credit Card Tips
          </Text>
          <Text size="small" data-cy={`${dataCy}-cc-tips-value`}>
            {centsToDollarsDisplay(totals.creditCardTips || 0)}
          </Text>
        </RowItem>
        <RowItem data-cy={`${dataCy}-credit-card-autograts`}>
          <Text size="small" data-cy={`${dataCy}-credit-card-autograts-label`}>
            Credit Card Autograts
          </Text>
          <Text size="small" data-cy={`${dataCy}-credit-card-autograts-value`}>
            {centsToDollarsDisplay(totals.creditCardAutograts || 0)}
          </Text>
        </RowItem>
        <RowItem data-cy={`${dataCy}-cash-tips`}>
          <Text size="small" data-cy={`${dataCy}-cash-tips-label`}>
            Cash Tips
          </Text>
          <Text size="small" data-cy={`${dataCy}-cash-tips-value`}>
            {centsToDollarsDisplay(totals.cashTips || 0)}
          </Text>
        </RowItem>
        <RowItem data-cy={`${dataCy}-cash-autograts`}>
          <Text size="small" data-cy={`${dataCy}-cash-autograts-label`}>
            Cash Autograts
          </Text>
          <Text size="small" data-cy={`${dataCy}-cash-autograts-value`}>
            {centsToDollarsDisplay(totals.cashAutograts || 0)}
          </Text>
        </RowItem>
        {!!creditCardPercentFee && (
          <RowItem data-cy={`${dataCy}-credit-card-fee`}>
            <Text size="small" data-cy={`${dataCy}-credit-card-fee-label`}>
              Credit Fee Deduction ({creditCardPercentFee}%)
            </Text>
            <Text size="small" data-cy={`${dataCy}-credit-card-fee-value`}>
              -{centsToDollarsDisplay(totals.creditCardFee || 0)}
            </Text>
          </RowItem>
        )}
        {totals.splitChecksTotal > 0 && (
          <RowItem data-cy={`${dataCy}-split-checks`}>
            <Text size="small" data-cy={`${dataCy}-split-checks-label`}>
              Split Checks
            </Text>
            <Text size="small" data-cy={`${dataCy}-split-checks-value`}>
              {centsToDollarsDisplay(totals.splitChecksTotal || 0)}
            </Text>
          </RowItem>
        )}
        {totals.ignoredChecksTotal > 0 && (
          <RowItem data-cy={`${dataCy}-ignored-checks`}>
            <Text size="small" data-cy={`${dataCy}-ignored-checks-label`}>
              Ignored Checks
            </Text>
            <Text size="small" data-cy={`${dataCy}-ignored-checks-value`}>
              {centsToDollarsDisplay(totals.ignoredChecksTotal || 0)}
            </Text>
          </RowItem>
        )}
      </ContentSection>
      <ContentSection data-cy={`${dataCy}-net-after-deductions-section`}>
        <RowItem data-cy={`${dataCy}-net-after-deduction`} className="total">
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-net-after-deductions-label`}>
            Net After Deductions
          </Text>
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-net-after-deductions-value`}>
            {centsToDollarsDisplay(totals.calculatedNet || 0)}
          </Text>
        </RowItem>
      </ContentSection>
    </Content>
  </Sidebar>
);

export default TipCalcOtherContributorsSidebar;
